// Client-side brief import: files never reach our servers unless the editor
// submits the resulting text as part of an AI story request.
const MAX_FILE_BYTES=4_000_000;
const MAX_BRIEF_CHARS=1800;
const decoder=new TextDecoder();
const wordNs='http://schemas.openxmlformats.org/wordprocessingml/2006/main';

function docxEntry(buffer){
 const view=new DataView(buffer);
 const end=Math.max(0,buffer.byteLength-65557);
 let eocd=-1;
 for(let i=buffer.byteLength-22;i>=end;i--){
  if(view.getUint32(i,true)===0x06054b50){eocd=i;break;}
 }
 if(eocd<0)throw new Error('This Word file is not a valid DOCX archive.');
 const entries=view.getUint16(eocd+10,true);
 let pointer=view.getUint32(eocd+16,true);
 for(let i=0;i<entries;i++){
  if(pointer+46>buffer.byteLength||view.getUint32(pointer,true)!==0x02014b50)
   throw new Error('The Word file ZIP index is invalid.');
  const compression=view.getUint16(pointer+10,true);
  const compressed=view.getUint32(pointer+20,true);
  const originalSize=view.getUint32(pointer+24,true);
  const nameLength=view.getUint16(pointer+28,true);
  const extra=view.getUint16(pointer+30,true);
  const comment=view.getUint16(pointer+32,true);
  const offset=view.getUint32(pointer+42,true);
  const name=decoder.decode(new Uint8Array(buffer,pointer+46,nameLength));
  pointer+=46+nameLength+extra+comment;
  if(name!=='word/document.xml')continue;
  if(originalSize>1_000_000)throw new Error('This Word document is too large for a short production brief.');
  if(offset+30>buffer.byteLength||view.getUint32(offset,true)!==0x04034b50)
   throw new Error('The Word document ZIP data is corrupt.');
  const start=offset+30+view.getUint16(offset+26,true)+view.getUint16(offset+28,true);
  if(start+compressed>buffer.byteLength)throw new Error('The Word document is incomplete.');
  return {compression,bytes:new Uint8Array(buffer,start,compressed)};
 }
 throw new Error('The DOCX has no readable document text.');
}
async function readDocx(file){
 const item=docxEntry(await file.arrayBuffer());
 let bytes=item.bytes;
 if(item.compression===8){
  if(typeof DecompressionStream==='undefined')throw new Error('This browser cannot unpack Word documents. Save your brief as TXT.');
  const decompressed=new Blob([bytes]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  bytes=new Uint8Array(await new Response(decompressed).arrayBuffer());
 }else if(item.compression!==0)throw new Error('This Word compression format is not supported.');
 if(bytes.length>1_000_000)throw new Error('The Word brief is too long.');
 const xml=decoder.decode(bytes);
 const documentXml=new DOMParser().parseFromString(xml,'application/xml');
 if(documentXml.querySelector('parsererror'))throw new Error('This Word file has invalid document XML.');
 const lines=[...documentXml.getElementsByTagNameNS(wordNs,'p')].map(p=>
  [...p.getElementsByTagNameNS(wordNs,'t')].map(node=>node.textContent||'').join('').trim());
 return lines.filter(Boolean).join('\n');
}
// Extract selectable text locally in the browser. PDF bytes are never
// uploaded to Takefold; only the edited brief is sent to AI on Generate.
async function readPdf(file){
 let pdfTask;
 try{
  const [pdfjs,worker]=await Promise.all([
   import('pdfjs-dist/legacy/build/pdf.mjs'),
   import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url')
  ]);
  pdfjs.GlobalWorkerOptions.workerSrc=worker.default;
  const bytes=new Uint8Array(await file.arrayBuffer());
  // Hard limits prevent large/malformed PDFs from monopolising an editor tab.
  pdfTask=pdfjs.getDocument({data:bytes,useSystemFonts:true});
  const document=await pdfTask.promise;
  if(document.numPages>30)throw new Error('This PDF has more than 30 pages. Import a shorter production brief.');
  const pages=[];
  for(let i=1;i<=document.numPages;i++){
   const page=await document.getPage(i);
   const content=await page.getTextContent();
   const lines=[];let line='';
   let lastBaseline=null;
   for(const item of content.items){
    if(typeof item.str!=='string')continue;
    const value=item.str.replace(/\s+/g,' ').trim();
    const baseline=Array.isArray(item.transform)?item.transform[5]:null;
    if(line && baseline!==null&&lastBaseline!==null&&Math.abs(baseline-lastBaseline)>3){
     lines.push(line.trim());line='';
    }
    if(value)line+=(line&& !/\s$/.test(line)?' ':'')+value;
    if(item.hasEOL){if(line.trim())lines.push(line.trim());line='';}
    lastBaseline=baseline;
   }
   if(line.trim())lines.push(line.trim());
   pages.push(lines.join('\n'));
   page.cleanup();
  }
  const extracted=pages.filter(Boolean).join('\n\n').trim();
  if(!extracted)throw new Error('No selectable text was found in this PDF. Scanned/image-only PDFs need OCR or a text-based brief.');
  return extracted;
 }catch(error){
  if(error?.name==='PasswordException')throw new Error('This PDF is password-protected. Unlock it before importing the brief.');
  if(error?.message?.includes('Invalid PDF')||error?.name==='InvalidPDFException')throw new Error('This PDF is corrupt or could not be read.');
  throw error;
 }finally{if(pdfTask)await pdfTask.destroy().catch(()=>{});}
}
const cleanRtf=text=>text.replace(/\\par\b/g,'\n').replace(/\\'[0-9a-f]{2}/gi,' ')
 .replace(/\\[a-z]+-?\d* ?/gi,'').replace(/[{}]/g,'').trim();

export async function readProductionBrief(file,{pdfReader=readPdf}={}){
 if(!file||!Number.isFinite(file.size)||file.size<1||file.size>MAX_FILE_BYTES)
  throw new Error('Choose a production brief smaller than 4 MB.');
 const ext=file.name?.toLowerCase().match(/\.[^.]+$/)?.[0];
 if(!['.txt','.md','.markdown','.rtf','.docx','.pdf'].includes(ext))
  throw new Error('Use a PDF, TXT, Markdown, RTF or Word DOCX production brief.');
 let text=ext==='.pdf'?await pdfReader(file):ext==='.docx'?await readDocx(file):await file.text();
 if(ext==='.rtf')text=cleanRtf(text);
 text=text.replace(/\r\n?/g,'\n').replaceAll(String.fromCharCode(0),'').trim();
 if(!text)throw new Error('No readable text was found in this production brief.');
 if(text.length>MAX_BRIEF_CHARS)
  throw new Error(`The imported brief contains ${text.length.toLocaleString()} characters, but AI supports ${MAX_BRIEF_CHARS.toLocaleString()}. Use a shorter document or extract just the relevant pages.`);
 return text;
}
